// src/services/procesarDevengoComision.js
//
// Helper de devengo automático — F3 del módulo Comisiones.
//
// Se invoca después de cada pago exitoso del cliente.
// Para cada comisión vinculada a la deuda evalúa si algún hito se cumple y,
// si así es, materializa una CxP just-in-time con tipoCosto:"comision_venta".
//
// GARANTÍAS:
//   • Idempotente: hito.devengado === true → se salta.
//   • No bloquea el flujo de pago: los errores se capturan y loguean sin relanzar.
//   • Comisiones canceladas se ignoran.
//
// SEMANTICA del campo comision.saldo (Audit C-2):
//   saldo = montoTotal - pagado  (lo que aun se le adeuda al comisionista)
// Este service NO toca saldo porque devengar no cambia "pagado", solo
// materializa hitos a CxPs. procesarPagoComision es quien actualiza saldo
// cuando se le paga al comisionista. cancelarComisionesEnCascada pone
// saldo=0 porque la comision dejo de ser una obligacion.
// El campo "por devengar" (montoTotal - devengado) es otra cosa, se calcula
// localmente en ComisionDetalle:541 y NO se persiste.
//
// PERMISO necesario en Firestore:
//   • leer   deudas/{id}                — deudas.ver
//   • leer   comisiones (where deudaId) — deudas.registrarPago
//   • crear  cxp_deudas                 — deudas.registrarPago + tipoCosto=="comision_venta"
//   • editar comisiones/{id}            — isComisionStateUpdate() (campos restringidos)

import {
  collection, doc, getDoc, getDocs, updateDoc, deleteDoc,
  query, where, serverTimestamp,
} from "firebase/firestore";
import { db } from "../Firebase/firebaseconfig";
import { evaluarTrigger } from "../helpers/comisionEsquemas";
import { createCxpDeuda } from "./cxpDeudaService";
import { registrarAuditoria } from "../helpers/auditoria";

/**
 * Evalúa y materializa hitos de devengo para todas las comisiones
 * vinculadas a una deuda, tras registrar un pago.
 *
 * @param {string} deudaId   — ID del documento en `deudas`
 * @param {string} [pagoId]  — pagoId del pago recién registrado (para trazabilidad)
 * @returns {Promise<{ devengados: number, errores: string[] }>}
 *   devengados: cuántos hitos se materializaron en esta llamada
 *   errores:    mensajes de error no fatales (no deben detener el flujo)
 */
export async function procesarDevengoComision(deudaId, pagoId = null) {
  const resultado = { devengados: 0, errores: [] };

  try {
    // ── 1. Cargar la deuda actualizada (ya incluye el nuevo pago) ──────────
    const deudaSnap = await getDoc(doc(db, "deudas", deudaId));
    if (!deudaSnap.exists()) {
      resultado.errores.push(`Deuda ${deudaId} no encontrada.`);
      return resultado;
    }
    const deuda = { id: deudaSnap.id, ...deudaSnap.data() };

    // ── 2. Comisiones vinculadas a esta deuda ─────────────────────────────
    const comisnQ = query(
      collection(db, "comisiones"),
      where("deudaId", "==", deudaId)
    );
    const comisSnap = await getDocs(comisnQ);
    if (comisSnap.empty) return resultado;

    const hoy = new Date().toISOString().split("T")[0]; // "YYYY-MM-DD"

    for (const comisDoc of comisSnap.docs) {
      const comision = { id: comisDoc.id, ...comisDoc.data() };

      // Ignorar canceladas
      if (comision.estado === "cancelada") continue;

      // ── Contexto ERP (propiedad / fraccionamiento / proyecto) ────────────
      // Necesario para que la CxP que se devengue afecte la utilidad correcta.
      // 1) Tomar de la comisión (docs nuevos lo guardan en crearComision).
      // 2) Fallback: leer del doc propiedad usando deuda.propiedadId
      //    (cubre comisiones legacy creadas sin contexto).
      let ctxPropId  = comision.propiedadId       || "";
      let ctxPropFolio = comision.propiedadFolio  || "";
      let ctxFraccId = comision.fraccionamientoId || "";
      let ctxFraccNm = comision.fraccionamientoNombre || "";
      let ctxProyId  = comision.proyectoId        || "";
      let ctxProyNm  = comision.proyectoNombre    || "";
      let ctxObraId  = comision.obraId            || "";
      let ctxObraNm  = comision.obraNombre        || "";

      // AUDIT A-5 [TODO]: lectura fallback de propiedad fuera de transaccion.
      // Si propiedad cambia de fraccionamiento/proyecto en paralelo (race muy
      // raro), la CxP nace mal-asignada al ERP. Fix pendiente: pasar todo el
      // flujo de devengo a runTransaction, incluyendo este getDoc.
      if ((!ctxProyId || !ctxFraccId) && deuda.propiedadId) {
        try {
          const propSnap = await getDoc(doc(db, "propiedades", deuda.propiedadId));
          if (propSnap.exists()) {
            const p = propSnap.data();
            if (!ctxPropId)  ctxPropId   = propSnap.id;
            if (!ctxPropFolio) ctxPropFolio = p.folio || p.codigoHumano || "";
            if (!ctxFraccId) ctxFraccId  = p.fraccionamientoId || "";
            if (!ctxFraccNm) ctxFraccNm  = p.fraccNombre || p.fraccionamientoNombre || "";
            if (!ctxProyId)  ctxProyId   = p.proyectoId || "";
            if (!ctxProyNm)  ctxProyNm   = p.proyectoNombre || "";
            if (!ctxObraId)  ctxObraId   = p.obraId || "";
            if (!ctxObraNm)  ctxObraNm   = p.obraNombre || "";
          }
        } catch (e) {
          console.warn("procesarDevengoComision: no se pudo leer propiedad fallback:", e);
        }
      }

      const hitos = Array.isArray(comision.hitos) ? comision.hitos : [];
      const hitosActualizados = hitos.map((h) => ({ ...h }));
      let nuevoDevengado = Number(comision.devengado || 0);
      let cambioEnEstaComision = false;

      // v1.2 EST-A.2: track de CxPs creadas en esta iteración de comisión.
      // Si el updateDoc(comision) final falla, debemos hacer rollback de las
      // CxPs creadas para evitar dejar CxPs huérfanas (sin hito devengado que
      // las refiera). Si no, la próxima ejecución crea CxPs duplicadas.
      const cxpsCreadasEnEstaComision = [];

      // ── 3. Evaluar cada hito aún no devengado ──────────────────────────
      for (let i = 0; i < hitos.length; i++) {
        const hito = hitos[i];
        if (hito.devengado) continue; // idempotente

        let cumple = false;
        let deudaPagoIdResuelto = pagoId;

        try {
          const evaluacion = evaluarTrigger(hito.trigger, deuda);
          cumple = evaluacion.cumple;
          if (evaluacion.deudaPagoId) deudaPagoIdResuelto = evaluacion.deudaPagoId;
        } catch (evalErr) {
          resultado.errores.push(
            `Comisión ${comision.id} hito ${i} (${hito.trigger}): error al evaluar — ${evalErr.message}`
          );
          continue;
        }

        if (!cumple) continue;

        // ── 4. Obtener proveedorId del espejo ────────────────────────────
        let proveedorEspejoId = "";
        try {
          const comisionistaSnap = await getDoc(
            doc(db, "comisionistas", comision.comisionistaId)
          );
          if (comisionistaSnap.exists()) {
            proveedorEspejoId = comisionistaSnap.data().proveedorEspejoId || "";
          }
        } catch {
          // Sin proveedor espejo todavía — createCxpDeuda usará nombre como fallback
        }

        // ── 5. Crear CxP just-in-time ────────────────────────────────────
        let cxpRef;
        try {
          cxpRef = await createCxpDeuda({
            proveedorId: proveedorEspejoId,
            proveedorNombre: comision.comisionistaNombre,
            importe: hito.monto,
            enganche: 0,
            mensualidad: 0,
            tipoCosto: "comision_venta",
            referencia: `${hito.etiqueta} — ${ctxPropFolio || deudaId}`,
            notas: `Comisión devengada automáticamente al cumplirse: "${hito.trigger}"`,
            // ── Contexto ERP: estos campos hacen que la CxP afecte la utilidad
            //    de la propiedad / fraccionamiento / proyecto correcto.
            propiedadId: ctxPropId,
            propiedadFolio: ctxPropFolio,
            fraccionamientoId: ctxFraccId,
            fraccionamientoNombre: ctxFraccNm,
            proyectoId: ctxProyId,
            proyectoNombre: ctxProyNm,
            obraId: ctxObraId,
            obraNombre: ctxObraNm,
            fechaVencimiento: hoy,
            contexto: {
              comisionId: comision.id,
              comisionistaId: comision.comisionistaId,
              deudaId,
              deudaOrigen: deudaId,
              hitoEtiqueta: hito.etiqueta,
              hitoIndex: i,
              propiedadId: ctxPropId,
              propiedadFolio: ctxPropFolio,
              fraccionamientoId: ctxFraccId,
              fraccionamientoNombre: ctxFraccNm,
              proyectoId: ctxProyId,
              proyectoNombre: ctxProyNm,
              deudaPagoId: deudaPagoIdResuelto || null,
            },
          });
        } catch (cxpErr) {
          resultado.errores.push(
            `Comisión ${comision.id} hito "${hito.etiqueta}": no se pudo crear CxP — ${cxpErr.message}`
          );
          continue; // no marcar el hito si falló la CxP
        }

        // ── 6. Marcar hito como devengado ────────────────────────────────
        hitosActualizados[i] = {
          ...hito,
          devengado: true,
          cxpId: cxpRef.id,
          deudaPagoId: deudaPagoIdResuelto || null,
          fechaDevengado: hoy,
        };
        nuevoDevengado += hito.monto;
        cambioEnEstaComision = true;
        resultado.devengados += 1;
        cxpsCreadasEnEstaComision.push(cxpRef.id); // EST-A.2: para rollback

        // Audit log (no fatal)
        registrarAuditoria({
          accion: "comisiones.devengar",
          entidadTipo: "comision",
          entidadId: comision.id,
          detalle: `Hito "${hito.etiqueta}" devengado — ${comision.comisionistaNombre}`,
          despues: {
            hitoEtiqueta: hito.etiqueta,
            monto: hito.monto,
            cxpId: cxpRef.id,
            trigger: hito.trigger,
            deudaId,
            pagoId: deudaPagoIdResuelto || null,
          },
        }).catch((e) => console.warn("Audit devengo error:", e));
      } // fin loop hitos

      if (!cambioEnEstaComision) continue;

      // ── 7. Actualizar doc comision ───────────────────────────────────────
      const todosDevengados = hitosActualizados.every((h) => h.devengado);
      try {
        await updateDoc(doc(db, "comisiones", comision.id), {
          hitos: hitosActualizados,
          devengado: Math.round(nuevoDevengado * 100) / 100,
          estado: todosDevengados ? "totalmenteDevengada" : comision.estado,
          updatedAt: serverTimestamp(),
        });
      } catch (updateErr) {
        // v1.2 EST-A.2: rollback de CxPs creadas en este intento.
        // Si no las borramos, quedan huérfanas (sin hito devengado que las
        // referencie) y la próxima ejecución crea CxPs duplicadas.
        const rollbackErrores = [];
        for (const cxpId of cxpsCreadasEnEstaComision) {
          try {
            await deleteDoc(doc(db, "cxp_deudas", cxpId));
          } catch (delErr) {
            rollbackErrores.push(`CxP ${cxpId}: ${delErr.message}`);
          }
        }
        if (rollbackErrores.length > 0) {
          resultado.errores.push(
            `Comisión ${comision.id}: updateDoc falló (${updateErr.message}) Y rollback INCOMPLETO de CxPs: ${rollbackErrores.join("; ")}. REQUIERE INTERVENCIÓN MANUAL: revisar cxp_deudas con comisionId=${comision.id} para detectar huérfanas.`
          );
          // Registrar auditoría especial — caso de inconsistencia que requiere intervención
          registrarAuditoria({
            accion: "comisiones.devengar.inconsistencia",
            entidadTipo: "comision",
            entidadId: comision.id,
            detalle: `Rollback parcial tras fallo de updateDoc. CxPs huérfanas: ${cxpsCreadasEnEstaComision.join(",")}`,
            despues: {
              cxpsCreadas: cxpsCreadasEnEstaComision,
              cxpsBorradas: cxpsCreadasEnEstaComision.length - rollbackErrores.length,
              error: updateErr.message,
            },
          }).catch(() => {});
        } else if (cxpsCreadasEnEstaComision.length > 0) {
          resultado.errores.push(
            `Comisión ${comision.id}: updateDoc falló — rollback exitoso de ${cxpsCreadasEnEstaComision.length} CxP(s). Reintenta el devengo.`
          );
        } else {
          resultado.errores.push(
            `No se pudo actualizar comisión ${comision.id}: ${updateErr.message}`
          );
        }
      }
    } // fin loop comisiones
  } catch (globalErr) {
    console.error("procesarDevengoComision error global:", globalErr);
    resultado.errores.push(`Error inesperado: ${globalErr.message}`);
  }

  return resultado;
}
